import { useState, useCallback, useLayoutEffect, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { store as accBootStore } from "../../../Lit/acc-boot/acc-boot.js";
import { useAppDispatch } from "../../../../store";
import type { ActionCreatorWithPayload } from "@reduxjs/toolkit";

interface UseActionReportTreeViewOptions {
  /** Whether the report data is loading */
  isLoading: boolean;
  /** Whether the report has data available */
  hasData: boolean;
  /** Redux action creator to set affAbosToExpand in the report's slice */
  setAffAbosToExpand: ActionCreatorWithPayload<string[]>;
  /** All ABOs in the current report data — used to keep highlights in sync when data changes (e.g. period change) */
  allAbos: Array<{ aff: string; abo: string }>;
}

interface UseActionReportTreeViewReturn {
  /** Whether the tree view is currently active */
  isTreeView: boolean;
  /** Toggle handler */
  handleToggle: () => void;
}

/**
 * Builds a stable serialized key from allAbos for effect dependency comparison.
 * Avoids infinite re-render loops caused by new array references on each render.
 */
const buildAbosKey = (allAbos: Array<{ aff: string; abo: string }>): string =>
  allAbos.map((i) => `${i.aff}-${i.abo}`).join(",");

/**
 * Reusable hook for hash-based tree/list toggle in action reports.
 * Manages URL hash state (#tree / #list), Redux affAbosToExpand state,
 * and the legacy accBootStore sync for the TreeView component.
 *
 * When the user is in tree view and the report data changes (e.g. period selector),
 * the highlighted ABOs are automatically re-synced.
 */
export const useActionReportTreeView = ({
  isLoading,
  hasData,
  setAffAbosToExpand,
  allAbos,
}: UseActionReportTreeViewOptions): UseActionReportTreeViewReturn => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isTreeView, setIsTreeView] = useState(hash.includes("tree"));
  const prevAbosKeyRef = useRef<string>("");

  // Auto-disable tree view if no data available
  useLayoutEffect(() => {
    if (isLoading) return;
    if (!hasData && isTreeView) {
      setIsTreeView(false);
      dispatch(setAffAbosToExpand([]));
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
      navigate("#list");
    }
  }, [isLoading, hasData, isTreeView, navigate, dispatch, setAffAbosToExpand]);

  // Re-sync highlighted ABOs when report data changes while in tree view
  // (e.g. user changes the period selector)
  const abosKey = useMemo(() => buildAbosKey(allAbos), [allAbos]);
  useEffect(() => {
    if (!isTreeView || isLoading || allAbos.length === 0) return;
    if (abosKey === prevAbosKeyRef.current) return;
    prevAbosKeyRef.current = abosKey;

    const affAbosToExpand = allAbos.map((i) => `${String(i.aff).padStart(3, "0")}-${String(i.abo)}`);
    dispatch(setAffAbosToExpand(affAbosToExpand));
    accBootStore.setState({ treeView: { affAbosToExpand } });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abosKey is the serialized form of allAbos; using it alone avoids re-firing on reference changes
  }, [isTreeView, isLoading, abosKey, dispatch, setAffAbosToExpand]);

  const handleToggle = useCallback(() => {
    if (isTreeView || hash.includes("tree")) {
      setIsTreeView(false);
      dispatch(setAffAbosToExpand([]));
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
      navigate("#list");
    } else {
      const affAbosToExpand = allAbos.map((i) => `${String(i.aff).padStart(3, "0")}-${String(i.abo)}`);
      dispatch(setAffAbosToExpand(affAbosToExpand));
      accBootStore.setState({ treeView: { affAbosToExpand } });
      setIsTreeView(true);
      navigate("#tree");
    }
  }, [isTreeView, hash, navigate, dispatch, setAffAbosToExpand, allAbos]);

  return { isTreeView, handleToggle };
};
